/** Categorizes project IDs based on the status of their associated "next actions". */
import { useMemo } from 'react';
import { useTasksContext } from '@/features/tasks/context/TasksContext.def';
import { useNextAction } from '@/features/tasks/hooks/status/use-task-next-action';
import { calculateProjectStates } from '@/features/tasks/utils/views/projectStatesUtils';

/**
 * @typedef {import('@/types').Task} Task
 */

/**
 * Custom hook that provides sets of project IDs based on the state of their "next action".
 * It consumes the `useNextAction` hook to determine which projects are considered
 * "planned" (next action has a scheduled date) or "waiting for" (next action is delegated).
 *
 * This hook correctly cascades both the "waiting for" and "planned" statuses up the entire
 * ancestor tree. If a "next action" subtask has a specific state, it traverses up via `parentId`
 * to mark all its parent tasks up to the project root with the same state.
 *
 * @returns {{plannedParentIds: Set<string>, waitingForParentIds: Set<string>}}
 *   An object containing two sets of project IDs.
 */
export const useProjectStates = () => {
  const { tasksMap } = useTasksContext();
  const nextActions = useNextAction();

  return useMemo(
    () => calculateProjectStates(tasksMap, nextActions),
    [nextActions, tasksMap],
  );
};
